import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Doer-wise performance scoring — leaderboard (management) + individual view.

const GRADE_COLOR = { 'A+': '#166534', A: '#0891b2', B: '#3B3A8F', C: '#9A6B00', D: '#A32D2D' };
const GRADE_LABEL = { 'A+': 'Outstanding', A: 'Excellent', B: 'Good', C: 'Average', D: 'Needs Improvement' };
const CATEGORY_LABEL = { tna: 'T&A', vps: 'VPS', grn: 'GRN', quality: 'Quality', procurement: 'Procurement' };
const ROLE_LABEL = { merchandiser: 'Merchandiser', procurement_officer: 'Procurement Officer', quality_incharge: 'Quality Incharge', ie_officer: 'IE Officer', production_incharge: 'Production Incharge' };

const thisMonth = () => new Date().toISOString().slice(0, 7);
const fmtD = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
const num = (v) => { const n = Number(v); return Number.isFinite(n) ? n : 0; };

const card = { background: 'white', borderRadius: 10, padding: '16px 20px', boxShadow: 'none', border: '1px solid #E8E7E7' };
const inputStyle = { padding: '7px 10px', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, boxSizing: 'border-box' };
const labelStyle = { fontSize: 10, color: '#888', fontWeight: 600, display: 'block', marginBottom: 3, textTransform: 'uppercase' };

const GradeBadge = ({ grade, size = 13 }) => grade
  ? <span style={{ padding: `${size / 4}px ${size}px`, borderRadius: 10, fontWeight: 800, fontSize: size, background: (GRADE_COLOR[grade] || '#888') + '22', color: GRADE_COLOR[grade] || '#888' }}>{grade}</span>
  : <span style={{ color: '#aaa' }}>—</span>;

// Two drill-down tabs shared by the doer detail and individual views.
function DrillDown({ detail }) {
  const [tab, setTab] = useState('category');
  const [open, setOpen] = useState({});
  const groups = tab === 'category'
    ? (detail.by_category || []).map(c => ({ key: c.activity_category, label: CATEGORY_LABEL[c.activity_category] || c.activity_category, ...c }))
    : (detail.by_gpo || []).map(g => ({ key: g.gpo_no, label: 'GPO ' + g.gpo_no, ...g }));
  const actsFor = (g) => (detail.activities || []).filter(a =>
    tab === 'category' ? a.activity_category === g.key : a.gpo_no === g.key);
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #eee', marginBottom: 12 }}>
        {[['category', 'A · By Activity Type'], ['gpo', 'B · By GPO']].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setOpen({}); }} style={{ padding: '9px 16px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: tab === k ? 700 : 400, color: tab === k ? '#3B3A8F' : '#888', borderBottom: tab === k ? '2px solid #3B3A8F' : '2px solid transparent' }}>{l}</button>
        ))}
      </div>
      {groups.length === 0 ? <div style={{ color: '#aaa', fontSize: 13, padding: 12 }}>No scored activities this month.</div>
        : groups.map(g => (
          <div key={g.key} style={{ border: '1px solid #eee', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
            <div onClick={() => setOpen(o => ({ ...o, [g.key]: !o[g.key] }))}
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: '#fafafa' }}>
              <span style={{ fontWeight: 700, fontSize: 13 }}>{open[g.key] ? '▾' : '▸'} {g.label} <span style={{ color: '#888', fontWeight: 400 }}>({g.c} activities)</span></span>
              <span style={{ fontWeight: 800, color: GRADE_COLOR[g.avg_score >= 90 ? 'A+' : g.avg_score >= 80 ? 'A' : g.avg_score >= 70 ? 'B' : g.avg_score >= 60 ? 'C' : 'D'] }}>{num(g.avg_score).toFixed(1)}</span>
            </div>
            {open[g.key] && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead><tr style={{ background: '#F7F6F3' }}>
                  {['Date', 'GPO', 'Activity', 'Planned', 'Actual', 'Var', 'Base', 'Bonus', 'Deduct', 'Final', 'Reason'].map(h => (
                    <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#555', fontWeight: 600, borderBottom: '1px solid #eee', fontSize: 10 }}>{h}</th>))}
                </tr></thead>
                <tbody>
                  {actsFor(g).map(a => (
                    <tr key={a.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                      <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>{fmtD(a.created_at)}</td>
                      <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 700, color: '#E22D2D' }}>{a.gpo_no}</td>
                      <td style={{ padding: '6px 8px' }}>{a.activity_name}</td>
                      <td style={{ padding: '6px 8px' }}>{fmtD(a.planned_date)}</td>
                      <td style={{ padding: '6px 8px' }}>{fmtD(a.actual_date)}</td>
                      <td style={{ padding: '6px 8px', color: a.days_variance > 0 ? '#A32D2D' : '#166534' }}>{a.days_variance > 0 ? '+' + a.days_variance : a.days_variance}</td>
                      <td style={{ padding: '6px 8px' }}>{a.base_score}</td>
                      <td style={{ padding: '6px 8px', color: '#166534' }}>{a.bonus_points ? '+' + a.bonus_points : '—'}</td>
                      <td style={{ padding: '6px 8px', color: '#A32D2D' }}>{a.deduction_points ? '-' + a.deduction_points : '—'}</td>
                      <td style={{ padding: '6px 8px', fontWeight: 800 }}>{a.final_score}</td>
                      <td style={{ padding: '6px 8px', color: '#888' }}>{a.score_reason || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ))}
    </div>
  );
}

export default function Performance() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMgmt = ['admin', 'management'].includes(user?.role);
  const [month, setMonth] = useState(thisMonth());
  const [roleFilter, setRoleFilter] = useState('');
  const [board, setBoard] = useState([]);
  const [selected, setSelected] = useState(null);     // selected doer {user_id, user_name}
  const [detail, setDetail] = useState(null);         // detail for selected / self
  const [myDetail, setMyDetail] = useState(null);
  const [myDue, setMyDue] = useState([]);
  const [view, setView] = useState(isMgmt ? 'team' : 'me');
  // Appraisal export (admin)
  const [expUser, setExpUser] = useState('');
  const [expFrom, setExpFrom] = useState('');
  const [expTo, setExpTo] = useState('');

  const loadBoard = useCallback(async () => {
    if (!isMgmt) return;
    const qs = new URLSearchParams({ month }); if (roleFilter) qs.set('role', roleFilter);
    const r = await api.get('/performance/leaderboard?' + qs.toString()).catch(() => ({ data: { data: [] } }));
    setBoard(r.data.data || []);
  }, [month, roleFilter, isMgmt]);
  useEffect(() => { loadBoard(); }, [loadBoard]);

  useEffect(() => {
    api.get(`/performance/user/${user.id}?month=${month}`).then(r => setMyDetail(r.data.data)).catch(() => {});
    api.get('/performance/my-due').then(r => setMyDue(r.data.data || [])).catch(() => {});
  }, [user.id, month]);

  const openDoer = async (d) => {
    setSelected(d); setDetail(null);
    const r = await api.get(`/performance/user/${d.user_id}?month=${month}`).catch(() => null);
    if (r) setDetail(r.data.data);
  };

  const exportAppraisal = async () => {
    if (!expUser) { alert('Select a user to export'); return; }
    const qs = new URLSearchParams({ user_id: expUser }); if (expFrom) qs.set('from', expFrom); if (expTo) qs.set('to', expTo);
    try {
      const r = await api.get('/performance/export?' + qs.toString(), { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url;
      a.download = (r.headers['content-disposition'] || '').match(/filename="(.+)"/)?.[1] || 'appraisal.xls';
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { alert('Export failed: ' + (e.response?.data?.error || e.message)); }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };
  const trendData = (myDetail?.trend || []).map(t => ({ month: t.m, score: num(t.avg_score), team: t.team_avg ? num(t.team_avg) : null }));

  return (
    <div style={{ minHeight: '100vh', background: 'transparent', fontFamily: 'inherit' }}>
      

      <div style={{ padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 20, color: '#1a1a2e' }}>🏅 Performance</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>Doer-wise scoring from T&A, GRN and quality activities. On-time work scores 100; bonuses and deductions per RSR rules.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
            {isMgmt && (
              <div style={{ display: 'flex', gap: 4 }}>
                {[['team', '👥 Team Leaderboard'], ['me', '🙋 My Score']].map(([k, l]) => (
                  <button key={k} onClick={() => setView(k)} style={{ padding: '7px 14px', borderRadius: 6, border: '1px solid #ddd', cursor: 'pointer', fontSize: 12, fontWeight: view === k ? 700 : 400, background: view === k ? '#3B3A8F' : 'white', color: view === k ? 'white' : '#666' }}>{l}</button>
                ))}
              </div>
            )}
            <div><label style={labelStyle}>Month</label><input type="month" style={inputStyle} value={month} onChange={e => { setMonth(e.target.value); setSelected(null); setDetail(null); }} /></div>
            {isMgmt && view === 'team' && (
              <div><label style={labelStyle}>Role</label>
                <select style={inputStyle} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
                  <option value="">All roles</option>
                  {Object.entries(ROLE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select></div>
            )}
          </div>
        </div>

        {/* ── MANAGEMENT: TEAM LEADERBOARD ── */}
        {isMgmt && view === 'team' && !selected && (
          <div>
            {board.length === 0 ? <div style={{ ...card, color: '#aaa', textAlign: 'center', padding: 40 }}>No scored activities for {month} yet.</div>
              : <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 14 }}>
                {board.map((d, i) => (
                  <div key={d.user_id} onClick={() => openDoer(d)} style={{ ...card, cursor: 'pointer', position: 'relative', borderLeft: `3px solid ${GRADE_COLOR[d.grade] || '#888'}` }}>
                    {i < 3 && <span style={{ position: 'absolute', top: 10, right: 12, fontSize: 18 }}>{['🥇', '🥈', '🥉'][i]}</span>}
                    <div style={{ fontWeight: 800, fontSize: 15 }}>{d.user_name || '—'}</div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{ROLE_LABEL[d.role_type] || d.role_type}</div>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 10 }}>
                      <span style={{ fontSize: 32, fontWeight: 800, color: GRADE_COLOR[d.grade] || '#888' }}>{Math.round(num(d.avg_score))}</span>
                      <GradeBadge grade={d.grade} />
                    </div>
                    <div style={{ fontSize: 11, color: '#666', marginTop: 8 }}>
                      {d.total_activities} activities · <strong style={{ color: '#166534' }}>{d.on_time_pct}% on time</strong>
                      {Number(d.not_done) > 0 && <span style={{ color: '#A32D2D' }}> · {d.not_done} not done</span>}
                    </div>
                  </div>
                ))}
              </div>}

            {/* Appraisal export — admin only */}
            {user?.role === 'admin' && (
              <div style={{ ...card, marginTop: 20 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#3B3A8F', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10 }}>📄 Appraisal Export (Excel)</div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 220 }}><label style={labelStyle}>User</label>
                    <select style={{ ...inputStyle, width: '100%' }} value={expUser} onChange={e => setExpUser(e.target.value)}>
                      <option value="">— select user —</option>
                      {board.map(d => <option key={d.user_id} value={d.user_id}>{d.user_name}</option>)}
                    </select></div>
                  <div><label style={labelStyle}>From</label><input type="date" style={inputStyle} value={expFrom} onChange={e => setExpFrom(e.target.value)} /></div>
                  <div><label style={labelStyle}>To</label><input type="date" style={inputStyle} value={expTo} onChange={e => setExpTo(e.target.value)} /></div>
                  <button onClick={exportAppraisal} style={{ padding: '8px 18px', background: '#166534', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontWeight: 700 }}>⬇ Export Appraisal Report</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── MANAGEMENT: DOER DETAIL ── */}
        {isMgmt && view === 'team' && selected && (
          <div>
            <button onClick={() => { setSelected(null); setDetail(null); }} style={{ marginBottom: 12, padding: '6px 14px', background: 'white', border: '1px solid #ddd', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>← Back to leaderboard</button>
            <div style={{ ...card, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 20 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 18 }}>{selected.user_name}</div>
                <div style={{ fontSize: 12, color: '#888' }}>{ROLE_LABEL[selected.role_type] || selected.role_type} · {month}</div>
              </div>
              {detail && <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 38, fontWeight: 800, color: GRADE_COLOR[detail.grade] || '#888' }}>{detail.avg_score !== null ? Math.round(detail.avg_score) : '—'}</span>
                <GradeBadge grade={detail.grade} size={15} />
              </div>}
            </div>
            <div style={card}>{detail ? <DrillDown detail={detail} /> : <div style={{ color: '#aaa' }}>Loading…</div>}</div>
          </div>
        )}

        {/* ── INDIVIDUAL VIEW ── */}
        {(view === 'me' || !isMgmt) && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, marginBottom: 16 }}>
              <div style={{ ...card, textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase' }}>My Score · {month}</div>
                <div style={{ fontSize: 56, fontWeight: 900, color: GRADE_COLOR[myDetail?.grade] || '#888', lineHeight: 1.1, marginTop: 8 }}>
                  {myDetail?.avg_score !== null && myDetail?.avg_score !== undefined ? Math.round(myDetail.avg_score) : '—'}</div>
                <div style={{ marginTop: 8 }}><GradeBadge grade={myDetail?.grade} size={16} /></div>
                {myDetail?.grade && <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>{GRADE_LABEL[myDetail.grade]}</div>}
              </div>
              <div style={card}>
                <div style={{ fontSize: 11, color: '#888', fontWeight: 600, textTransform: 'uppercase', marginBottom: 8 }}>Last 6 Months Trend vs Team Average</div>
                {trendData.length === 0 ? <div style={{ color: '#aaa', fontSize: 13, padding: 20 }}>No history yet.</div>
                  : <ResponsiveContainer width="100%" height={180}>
                    <ComposedChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="month" fontSize={11} /><YAxis domain={[0, 100]} fontSize={11} />
                      <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="score" name="My Score" fill="#3B3A8F" radius={[4, 4, 0, 0]} barSize={28} />
                      <Line dataKey="team" name="Team Avg" stroke="#E22D2D" strokeWidth={2} dot={{ r: 3 }} />
                    </ComposedChart>
                  </ResponsiveContainer>}
              </div>
            </div>

            {myDue.length > 0 && (
              <div style={{ ...card, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#9A6B00', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>⏰ My Activities Due This Week</div>
                {myDue.map((d, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
                    <span><strong style={{ fontFamily: 'monospace', color: '#E22D2D' }}>{d.gpo_no}</strong> · {d.activity_name}</span>
                    <span style={{ color: new Date(d.planned_date) < new Date() ? '#A32D2D' : '#888', fontWeight: 600 }}>{fmtD(d.planned_date)}</span>
                  </div>
                ))}
              </div>
            )}

            <div style={card}>{myDetail ? <DrillDown detail={myDetail} /> : <div style={{ color: '#aaa' }}>Loading…</div>}</div>
          </div>
        )}
      </div>
    </div>
  );
}
